import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Sheet from "../Sheet.jsx";
import { useQuery } from "../../lib/dataClient.js";
import { sumSeries } from "../../lib/renderers.js";
import { observeSettledSize } from "../../lib/settledResize.js";
import { DIMENSIONS, encodeScopePath } from "../../lib/dimensionDefs.js";
import {
  compositionDimsFor,
  scopeNounFor,
  compositionHeadingFor,
  concentrationVerdict,
  computeLift,
  topStandout,
  nextPin,
  resolveScopeDenominator,
} from "../../lib/compositionModel.js";
import { useScopeBaseline } from "../../lib/useScopeBaseline.js";
import { useCompositionFlows } from "../../lib/useCompositionFlows.js";
import { CompositionPanel } from "./Composition.jsx";
import CompositionFlowOverlay from "./CompositionFlowOverlay.jsx";

// ScopeComposition — the "who/what drives this scope" cross-tab, generalized off
// the cluster Detail lens's composition panel to EVERY drilled scope.
//
// It's a reframe of the sidebar's own breakdown data — the same ranked members
// the "Break down by" panes list — pulled into one juxtaposed grid with a
// concentration verdict + a "stands out" lift callout up front.
//
// CORRELATION (pin-to-reweight): clicking a member focuses it — the OTHER
// columns re-scope in place to (this scope ∩ member), so you can read "what
// models does MITCH use FOR repo work" without leaving the endpoint. Clicking
// again, or the header's clear, unpins; "drill in" commits the intersection as
// a real navigation. Lightweight: no new query type, just an extra scope step on
// the sibling columns.
//
// Self-owned data (per the lens data-ownership note in ScopeOverviewBody): its
// queries live here, keyed by the LensHost lens id, not threaded through ctx.
// Scope-aware in which dims it profiles by and how the verdict reads — see
// compositionModel.js. Works at every scope including tenant ([]), where it's
// the org's overall makeup.

const SLOTS = 3;   // columns — fixed so the useQuery hooks keep a constant order
const ROWS = 12;   // members listed per column

export default function ScopeComposition({ scopePath = [], range, onNavigatePath, preferDims }) {
  // Columns are data-driven when the caller passes a distinctiveness ranking
  // (the most-discriminating dims for this scope); otherwise the stable
  // per-terminal default. See useScopeDistinctiveness + ScopeAnatomyBody.
  const dims = useMemo(
    () => (preferDims && preferDims.length ? preferDims.slice(0, SLOTS) : compositionDimsFor(scopePath, SLOTS)),
    // Depend on preferDims by VALUE (its joined string), not identity — the
    // caller re-derives the array each render, so an identity dep would recompute
    // every time. exhaustive-deps can't see through the join(), so it's silenced.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preferDims && preferDims.join(","), scopePath],
  );
  const subject = scopeNounFor(scopePath);
  const heading = compositionHeadingFor(scopePath);
  const enabledBlock = dims.length > 0;

  // Pin (correlation focus). Cleared whenever the committed scope changes so a
  // stale focus never bleeds across pages. The clear runs in an effect (one
  // render late), so we ALSO stamp the pin with the scopeKey it was set on and
  // read it through `activePin` — a pin from a prior scope reads as null in the
  // SAME render, so no stale-intersection query fires in the gap before the
  // effect clears it. (The effect still clears the underlying state so a
  // returning navigation can't reactivate it.)
  const [pin, setPin] = useState(null); // { dim, id, label, scopeKey }
  const [hovered, setHovered] = useState(null); // { col, row } — focused flow cell
  const scopeKey = useMemo(() => encodeScopePath(scopePath), [scopePath]);
  useEffect(() => { setPin(null); setHovered(null); }, [scopeKey]);
  const activePin = pin && pin.scopeKey === scopeKey ? pin : null;
  const pinStep = activePin ? { dim: activePin.dim, ids: [activePin.id], id: activePin.id } : null;

  // Scope total (prompts in scope) — the unpinned share/verdict denominator.
  const { data: bundle } = useQuery(
    "getOverviewActivityBundle",
    { scopePath, bucketCount: 1, topN: 1, range },
    { enabled: enabledBlock },
  );
  const scopeTotal = useMemo(() => sumSeries(bundle?.prompts?.total), [bundle]);

  // Intersection total — the denominator for the re-scoped columns while pinned.
  const { data: pinBundle } = useQuery(
    "getOverviewActivityBundle",
    { scopePath: activePin ? [...scopePath, pinStep] : scopePath, bucketCount: 1, topN: 1, range },
    { enabled: enabledBlock && Boolean(activePin) },
  );
  const pinTotal = useMemo(() => sumSeries(pinBundle?.prompts?.total), [pinBundle]);

  // Fixed-shape entry fetches. A column for a dim OTHER than the pinned one
  // re-scopes to (scope ∩ pin); the pinned dim's own column stays scope-level
  // (all members, the pinned one highlighted).
  const slots = [];
  for (let i = 0; i < SLOTS; i++) {
    const dimKey = dims[i] || null;
    const rescoped = Boolean(activePin && dimKey && dimKey !== activePin.dim);
    const parentScopePath = rescoped ? [...scopePath, pinStep] : scopePath;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, loading } = useQuery(
      "getDimensionEntries",
      { dimKey: dimKey || "endpoints", parentScopePath, limit: ROWS, range },
      { enabled: enabledBlock && Boolean(dimKey) },
    );
    // Denominator + lift-readiness. While a freshly-applied pin's intersection
    // total is still loading (pinTotal 0), hold scopeTotal and suppress lift so
    // no wrong-baseline badge flashes. See resolveScopeDenominator.
    const { effectiveTotal, liftReady } = resolveScopeDenominator({ rescoped, pinTotal, scopeTotal });
    slots.push({ dimKey, data, loading, rescoped, effectiveTotal, liftReady });
  }

  // Verdict + standout describe the RESTING scope, so they read off the base
  // (unpinned) columns and are shown only when unpinned — while a focus is
  // active, the pin chip + re-scoped columns carry the intersection story.
  const primary = slots[0];
  const verdict = useMemo(() => {
    if (activePin || !primary?.dimKey) return null;
    return concentrationVerdict({
      entries: primary.data?.entries,
      total: primary.data?.total,
      scopeTotal,
      dimKey: primary.dimKey,
      subject,
    });
  }, [activePin, primary?.dimKey, primary?.data, scopeTotal, subject]);

  // Baseline lift — over-/under-index vs the org. Disabled at tenant (a scope
  // compared to itself). Baseline stays the ORG even while pinned, so a re-scoped
  // column reads "within the intersection, X over-indexes N× vs the whole org".
  const baseline = useScopeBaseline(dims, range, scopePath.length > 0);

  const { liftBySlot, standout } = useMemo(() => {
    const bySlot = new Map();
    const columns = [];
    for (const s of slots) {
      if (!s.dimKey) continue;
      const baseMembers = baseline.baseByDim.get(s.dimKey);
      const m = new Map();
      const rows = [];
      for (const e of s.data?.entries || []) {
        const id = String(e.id);
        const lift = computeLift({
          scopeActivity: Number(e.activity) || 0,
          scopeTotal: s.effectiveTotal,
          baseActivity: baseMembers?.get(id) || 0,
          baseTotal: baseline.baseTotal,
        });
        if (lift != null) m.set(id, lift);
        rows.push({ label: e.label, lift });
      }
      bySlot.set(s.dimKey, m);
      columns.push({ dimLabel: DIMENSIONS[s.dimKey]?.label || s.dimKey, rows });
    }
    return { liftBySlot: bySlot, standout: topStandout(columns) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...slots.map((s) => s.data), ...slots.map((s) => s.effectiveTotal), baseline]);

  // ── Flow ribbons (sankey) between adjacent columns. Only in the resting
  // (unpinned) state — while pinned the columns are intersections and cross-
  // flows read as noise. Measure the grid so the SVG overlay can attach ribbons
  // at real column edges + row centers.
  const gridRef = useRef(null);
  const [geom, setGeom] = useState(null);
  const measure = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const gr = grid.getBoundingClientRect();
    // Measure EACH column's own rows — a sub-label header or a shorter column
    // shifts row centers, so a single grid-wide rowH would misalign the ribbon
    // attach points. Row y-centers are read per column, index-aligned to the
    // column's entries.
    const cols = [...grid.querySelectorAll("[data-comp-col]")].map((el) => {
      const r = el.getBoundingClientRect();
      const rows = [...el.querySelectorAll("[data-comp-row]")].map((rowEl) => {
        const rr = rowEl.getBoundingClientRect();
        return { y: rr.top - gr.top + rr.height / 2 };
      });
      return { leftX: r.left - gr.left, rightX: r.right - gr.left, rows };
    });
    setGeom({ width: gr.width, height: gr.height, columns: cols });
  }, []);
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return undefined;
    measure();
    return observeSettledSize(grid, measure);
  }, [measure]);
  // Re-measure when the rows themselves change (counts / dims / pin shift layout).
  const layoutSig = `${slots.map((s) => s.data?.entries?.length ?? 0).join(",")}|${dims.join(",")}|${activePin ? "p" : ""}`;
  useEffect(() => { measure(); }, [layoutSig, measure]);

  // Shown columns' top ids → co-occurrence flows (disabled while pinned).
  const flowColumns = useMemo(
    () => (activePin ? [] : slots.filter((s) => s.dimKey).map((s) => ({
      dimKey: s.dimKey, ids: (s.data?.entries || []).slice(0, ROWS).map((e) => String(e.id)),
    }))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activePin, ...slots.map((s) => s.data), dims],
  );
  const flows = useCompositionFlows(scopePath, flowColumns, range);

  const columnGeom = useMemo(() => {
    if (!geom || activePin) return null;
    const shown = slots.filter((s) => s.dimKey);
    const cg = shown.map((s, i) => {
      const col = geom.columns[i];
      if (!col) return null;
      // y comes from the column's own measured row (index-aligned); left
      // undefined if a row wasn't measured yet — the overlay skips those.
      const rows = (s.data?.entries || []).slice(0, ROWS).map((e, r) => ({
        y: col.rows[r]?.y,
        value: Math.max(0, Number(e.activity) || 0),
      }));
      return { leftX: col.leftX, rightX: col.rightX, rows };
    });
    return cg.every(Boolean) ? cg : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geom, activePin, ...slots.map((s) => s.data)]);

  // Clicking a member focuses it (toggles the pin). Refocusing to a different
  // member just replaces the pin — a fluid "what goes with this?" sweep. See
  // nextPin for the toggle-off / replace transition. Stamp the scope it was set
  // on so a scope change invalidates it (activePin) before the clear effect runs.
  const onPick = (dimKey) => (id, entry) => {
    const sid = String(id);
    setPin((cur) => {
      const np = nextPin(cur, dimKey, sid, entry?.label);
      return np ? { ...np, scopeKey } : null;
    });
  };
  const drillIntoPin = () => { if (pinStep) onNavigatePath?.([...scopePath, pinStep]); };

  if (!enabledBlock) return null;

  return (
    <Sheet eyebrow="Composition" title={heading} tight>
      {verdict ? (
        <span className="block pb-[4px] text-[12px] leading-[1.45] text-[color:var(--muted)]">{verdict}</span>
      ) : null}
      {/* The one thing worth noticing — the strongest over-index vs the org. */}
      {!activePin && standout ? (
        <span className="block pb-[6px] text-[12px] leading-[1.45] text-[color:var(--ink)]">
          Stands out:{" "}
          <span className="text-[color:var(--green)]">{standout.label}</span> over-indexes{" "}
          <span className="tabular-nums text-[color:var(--green)]">{standout.lift >= 10 ? "10+" : standout.lift.toFixed(1)}×</span>{" "}
          on {subject} vs the org ({standout.dimLabel.toLowerCase()}).
        </span>
      ) : null}

      {/* Correlation focus chip — what the other columns are now sliced by, with
          the escape hatches: drill in (commit) / clear (unpin). */}
      {activePin ? (
        <div className="mb-[8px] flex flex-wrap items-center gap-[8px] text-[11px]">
          <span className="text-[color:var(--muted)]">Focused on</span>
          <span className="inline-flex items-center gap-[6px] rounded-[2px] border border-[color:var(--green)] bg-[color:var(--control-bg)] px-[7px] py-[2px]">
            <span className="text-[10px] uppercase tracking-[0.05em] text-[color:var(--eyebrow)]">{DIMENSIONS[activePin.dim]?.label || activePin.dim}</span>
            <span className="max-w-[220px] truncate font-medium text-[color:var(--ink)]">{activePin.label}</span>
          </span>
          <button type="button" onClick={drillIntoPin} className="text-[color:var(--green)] hover:underline cursor-pointer">
            drill in →
          </button>
          <button type="button" onClick={() => setPin(null)} className="text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer">
            clear ✕
          </button>
        </div>
      ) : null}

      {/* The cross-tab. Columns follow the dim count so a scope with only two
          spare dims doesn't leave a dead column. Each row carries a lift badge
          vs the org and is clickable to focus (correlate). Flow ribbons connect
          co-occurring members across adjacent columns in the gutters (behind the
          rows, so the in-row sparklines stay untouched). */}
      <div className="relative">
        {columnGeom && flows.length ? (
          <CompositionFlowOverlay width={geom.width} height={geom.height} columnGeom={columnGeom} flows={flows} hovered={hovered} />
        ) : null}
        <div
          ref={gridRef}
          onMouseLeave={() => setHovered(null)}
          className="relative z-[1] grid gap-x-[40px] gap-y-[14px]"
          style={{ gridTemplateColumns: `repeat(${dims.length}, minmax(0, 1fr))` }}
        >
          {slots.filter((s) => s.dimKey).map((s, colIdx) => (
            <CompositionPanel
              key={s.dimKey}
              title={DIMENSIONS[s.dimKey]?.label || s.dimKey}
              subLabel={s.rescoped && activePin ? `within ${activePin.label}` : null}
              data={s.data}
              loading={s.loading}
              scopeTotal={s.effectiveTotal}
              rows={ROWS}
              onDrill={onPick(s.dimKey)}
              liftById={s.liftReady ? liftBySlot.get(s.dimKey) : undefined}
              pinnedId={activePin && activePin.dim === s.dimKey ? activePin.id : null}
              onRowHover={activePin ? undefined : (r) => setHovered({ col: colIdx, row: r })}
            />
          ))}
        </div>
      </div>
    </Sheet>
  );
}
